import { api } from "./client";

/**
 * Настройките за бакшиш — `GET`/`PUT /payment-methods/tip-settings`
 * (BE `app/features/tips/`, docs/tips.md там). JSON-ът е camelCase, а
 * парите и процентът идват като НИЗ („20.00") — не ги декларирай като число.
 *
 * Пътят е под огледалния префикс `/payment-methods` (`lib/edge/edgeMirror.ts`):
 * без интернет модалът „Закрий" чете последния облачен отговор от касата,
 * а самата каса проверява бакшиша по подписания си snapshot.
 */
export const TIP_SETTINGS_PATH = "/payment-methods/tip-settings";

/** Под `["payment-methods"]` — CRUD-ът на методите (инвалидира префикса)
 *  опреснява и настройките: изтрит метод изчезва от разрешените. */
export const TIP_SETTINGS_QUERY_KEY = ["payment-methods", "tip-settings"] as const;

/** Името на позицията и бележката под нея (BE `TIP_NAME_MAX`/`TIP_NOTE_MAX`). */
export const TIP_NAME_MAX = 36;
export const TIP_NOTE_MAX = 90;

export interface TipSettings {
  enabled: boolean;
  receiptNameBg: string;
  /** null — не е попълнено (документите ползват българското). */
  receiptNameEn: string | null;
  receiptNoteBg: string | null;
  /** Разрешените методи; никога 3 („Смесено"), само съществуващи. */
  paymentMethodIds: number[];
  /** „0.00".."100.00" — дял за разходи, НЕ данъчна ставка. */
  expenseSharePercent: string;
  /** „Приспадай картовия бакшиш при отчитане". */
  deductCardTips: boolean;
  /** null — без предупреждение. */
  warnAboveAmount: string | null;
  maxAmount: string;
  /** „A" | „B" | „C" (`lib/tipTaxGroups.ts`) — групата на реда в касовия бон. */
  fiscalTaxGroup: string;
  /** Само за четене — следва групата: А → `voluntary_tip_no_vat`, Б/В → `taxable`. */
  invoiceVatTreatment: string;
  updatedAt: string | null;
}

/** Тялото на `PUT` — без полетата само за четене. `fiscalTaxGroup` е по избор:
 *  без него BE пази записаната група; непозната стойност → 422
 *  `tip_tax_group_invalid` (`field: "fiscalTaxGroup"`). */
export type TipSettingsSaveIn = Omit<
  TipSettings,
  "fiscalTaxGroup" | "invoiceVatTreatment" | "updatedAt"
> & { fiscalTaxGroup?: string };

/** Отговорът при отказан запис (422): `detail = {code, field, bg, en}`. */
export interface TipSettingsErrorDetail {
  code: string;
  field: string;
  bg: string;
  en: string;
}

const statusOf = (error: unknown): number | undefined =>
  (error as { response?: { status?: number } })?.response?.status;

/**
 * Настройките или `null`, когато сървърът изобщо не ги познава (404/405 —
 * по-стар BE): тогава бакшиш няма. Без това стар сървър тихо би пренебрегнал
 * полетата за бакшиша в тялото на закриването и би закрил без него.
 */
export const getTipSettings = async (): Promise<TipSettings | null> => {
  try {
    const response = await api.get<TipSettings>(TIP_SETTINGS_PATH);
    return response.data;
  } catch (error) {
    const status = statusOf(error);
    if (status === 404 || status === 405) return null;
    throw error;
  }
};

/** Запис (право `manage_payment_types`); връща записаното. */
export const saveTipSettings = async (
  data: TipSettingsSaveIn,
): Promise<TipSettings> => {
  const response = await api.put<TipSettings>(TIP_SETTINGS_PATH, data);
  return response.data;
};
